import { useRef, useEffect } from "react";
import gsap from "gsap";
import ScrollTrigger from "gsap/ScrollTrigger";

interface StorySectionProps {
  description: string | null;
  coverImage?: string | null;
}

export function StorySection({ description, coverImage }: StorySectionProps) {
  const sectionRef = useRef<HTMLElement>(null);
  const bgRef = useRef<HTMLDivElement>(null);
  const cardRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!sectionRef.current) return;

    if (bgRef.current) {
      gsap.to(bgRef.current, {
        yPercent: -20,
        ease: "none",
        scrollTrigger: {
          trigger: sectionRef.current,
          start: "top bottom",
          end: "bottom top",
          scrub: true,
        },
      });
    }

    if (cardRef.current) {
      gsap.fromTo(
        cardRef.current,
        { opacity: 0, y: 40 },
        {
          opacity: 1,
          y: 0,
          duration: 1,
          ease: "power2.out",
          scrollTrigger: {
            trigger: sectionRef.current,
            start: "top 70%",
            toggleActions: "play none none none",
          },
        }
      );
    }
  }, []);

  if (!description) return null;

  const preview = description.length > 300
    ? description.slice(0, 297) + "..."
    : description;

  return (
    <section
      ref={sectionRef}
      className="relative flex min-h-screen items-center justify-center overflow-hidden bg-[#1b1817] px-6 py-24"
    >
      <div
        ref={bgRef}
        className="absolute inset-[-20%] bg-cover bg-center"
        style={
          coverImage
            ? { backgroundImage: `url(${coverImage})` }
            : { background: "linear-gradient(180deg, #2a1f1b 0%, #1b1817 100%)" }
        }
      />
      <div className="absolute inset-0 bg-[#1b1817]/70" />

      <div
        ref={cardRef}
        className="relative z-10 max-w-lg rounded-2xl border border-white/10 bg-[#1b1817]/80 p-8 backdrop-blur-sm sm:p-12"
      >
        <h2
          className="mb-6 font-serif text-3xl font-bold italic text-white sm:text-4xl"
          style={{ fontFamily: "var(--font-serif, 'Playfair Display', serif)" }}
        >
          "Hikayemiz"
        </h2>
        <p className="text-base leading-relaxed text-white/70 sm:text-lg">
          {preview}
        </p>
      </div>
    </section>
  );
}
